"use client";

import React, { useState, useCallback, useEffect } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { useAccountInfoDialog } from "./useAccountInfoDialog";
import { Dialog, DialogTitle, DialogContent, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import s from "./styles";
import { logjs, providers } from "@lib/client";

const log = new logjs("login/AccountInfoDialog");

const { useWebAppContext } = providers;

/**
 * アカウント情報ダイアログコンポーネント
 * 会員情報変更機能を提供する
 */
const AccountInfoDialog = ({ open, onClose, initState = {} }) => {
  const router = useRouter();
  // Webアプリケーションのコンテキストを取得
  const webAppContext = useWebAppContext();
  const { state, actions } = webAppContext || {};

  const [form, formProps] = useAccountInfoDialog(initState, state, actions);
  const [changeFlag, setChangeFlag] = useState(0);

  useEffect(() => {
    if (open) {
      formProps.init();
    }
  }, [open]);

  // パスワード変更の処理
  const handleChangePassword = useCallback(async () => {
    const result = await formProps.changePassword();
    if (result?.result === true) {
      actions.showSuccess("変更しました。");
      setChangeFlag(1);
    } else {
      const errorMessage = result?.error || "保存中にエラーが発生しました";
      actions.showError(errorMessage);
    }
  }, [formProps]);

  // ユーザー情報変更の処理
  const handleChangeUserInfo = useCallback(async () => {
    const result = await formProps.changeInfo();
    if (result?.result === true) {
      setChangeFlag(1);
      actions.showSuccess("変更しました。");
    } else {
      const errorMessage = result?.error || "保存中にエラーが発生しました";
      actions.showError(errorMessage);
    }
  }, [formProps]);

  // ダイアログを閉じる処理
  const handleClose = useCallback(() => {
    onClose();
    if (changeFlag !== 0) {
      router.refresh();
    }
  }, [onClose, changeFlag]);

  // 郵便番号の変更処理
  const handleZipCodeChange = useCallback(
    (e) => {
      const zipCode = e.target.value.replace(/[^0-9]/g, "").slice(0, 7);
      formProps.setForm({ ...form, zipCode: zipCode });
      if (zipCode.length === 7) formProps.searchAddress(zipCode);
    },
    [formProps]
  );

  // 必須マークの画像
  const requiredMarkSrc = "/images/must.png";

  // @@@ OEM対応 要確認
  function getOemStyles() {
    if (state?.user?.is_kyoto_seibi) return "accountinfo-container--kyoto-seibi";
    return undefined;
  }

  const isModal = false;
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      // PaperProps={{
      //   style: {
      //     maxHeight: "90vh",
      //     margin: "20px",
      //   },
      // }}
      hideBackdrop={isModal}
      style={!isModal ? { pointerEvents: "none" } : { pointerEvents: "auto" }}
      BackdropProps={
        !isModal
          ? {
              style: { pointerEvents: "none" }, // 背景は操作不可にする
            }
          : {}
      }
      PaperProps={
        !isModal
          ? {
              style: { pointerEvents: "auto" }, // ダイアログ自体は操作可能にする
              sx: {
                maxHeight: "90vh",
                margin: "20px",
              },
            }
          : {}
      }
    >
      <s.AccountInfoContainer>
        <s.AccountInfoTitle>
          会員情報変更
          <s.CloseButton onClick={handleClose}>
            <Image src="/images/window_close.png" alt="閉じる" width={40} height={40} />
          </s.CloseButton>
        </s.AccountInfoTitle>

        <DialogContent style={{ padding: "0" }}>
          <s.ResponsiveContainer>
            {/* エラーメッセージ */}
            {form.error && <s.ErrorMessage>{form.error}</s.ErrorMessage>}

            {/* 成功メッセージ */}
            {form.success && <s.SuccessMessage>変更しました。</s.SuccessMessage>}

            {/* 自社情報セクション */}
            <s.SectionTitle className={getOemStyles()}>自社情報</s.SectionTitle>
            <s.InfoTable>
              <tbody>
                <tr>
                  <s.InfoTableHeader className={getOemStyles()}>所属グループ</s.InfoTableHeader>
                  <s.InfoTableCell>{form.groupName}</s.InfoTableCell>
                </tr>
                <tr>
                  <s.InfoTableHeader className={getOemStyles()}>自社名</s.InfoTableHeader>
                  <s.InfoTableCell>{form.corpName}</s.InfoTableCell>
                </tr>
                <tr>
                  <s.InfoTableHeader className={getOemStyles()}>郵便番号</s.InfoTableHeader>
                  <s.InfoTableCell>{form.zipCode}</s.InfoTableCell>
                </tr>
                <tr>
                  <s.InfoTableHeader className={getOemStyles()}>住所</s.InfoTableHeader>
                  <s.InfoTableCell>
                    <s.AddressContainer>
                      <s.AddressLine>都道府県：{form.todofuken}</s.AddressLine>
                      <s.AddressLine>地区町村：{form.jyusho1}</s.AddressLine>
                      <s.AddressLine>番地&emsp;&emsp;：{form.jyusho2}</s.AddressLine>
                      <s.AddressLine>ビル・建物名：{form.jyusho3}</s.AddressLine>
                    </s.AddressContainer>
                  </s.InfoTableCell>
                </tr>
                <tr>
                  <s.InfoTableHeader className={getOemStyles()}>電話番号</s.InfoTableHeader>
                  <s.InfoTableCell>{form.tel}</s.InfoTableCell>
                </tr>
                <tr>
                  <s.InfoTableHeader className={getOemStyles()}>FAX</s.InfoTableHeader>
                  <s.InfoTableCell>{form.fax}</s.InfoTableCell>
                </tr>
              </tbody>
            </s.InfoTable>

            {/* ユーザー情報セクション */}
            <s.SectionTitle className={getOemStyles()}>ユーザ情報</s.SectionTitle>
            <s.InfoTable>
              <tbody>
                <tr>
                  <s.InfoTableHeader className={getOemStyles()}>
                    担当者名
                    <Image src={requiredMarkSrc} alt="必須" width={41} height={16} style={{ marginLeft: "5px", float: "right", verticalAlign: "middle" }} />
                  </s.InfoTableHeader>
                  <s.InfoTableCell>{formProps.input_UserName()}</s.InfoTableCell>
                </tr>
                <tr>
                  <s.InfoTableHeader className={getOemStyles()}>
                    メールアドレス
                    <Image src={requiredMarkSrc} alt="必須" width={41} height={16} style={{ marginLeft: "5px", float: "right", verticalAlign: "middle" }} />
                  </s.InfoTableHeader>
                  <s.InfoTableCell>{formProps.input_Email()}</s.InfoTableCell>
                </tr>
                <tr>
                  <s.InfoTableHeader className={getOemStyles()}>ログインID</s.InfoTableHeader>
                  <s.InfoTableCell>{form.UserID}</s.InfoTableCell>
                </tr>
              </tbody>
            </s.InfoTable>

            <s.ButtonContainer>
              <s.ChangeButton className={getOemStyles()} onClick={handleChangeUserInfo} disabled={form.isSubmitting}>
                ユーザ情報を変更
              </s.ChangeButton>
            </s.ButtonContainer>

            {/* パスワードセクション */}
            <s.SectionTitle className={getOemStyles()}>パスワード</s.SectionTitle>
            <s.InfoTable>
              <tbody>
                <tr>
                  <s.InfoTableHeader className={getOemStyles()}>
                    古いパスワード
                    <Image src={requiredMarkSrc} alt="必須" width={41} height={16} style={{ marginLeft: "5px", float: "right", verticalAlign: "middle" }} />
                  </s.InfoTableHeader>
                  <s.InfoTableCell>
                    {formProps.input_password({
                      name: "oldPassword",
                      label: "",
                    })}
                    {/* <s.InputField type="password" value={form.oldPassword || ""} onChange={(e) => formProps.setForm({ ...form, oldPassword: e.target.value })} style={{ imeMode: "inactive" }} /> */}
                    <s.PasswordNote>※半英数字8文字</s.PasswordNote>
                  </s.InfoTableCell>
                </tr>
                <tr>
                  <s.InfoTableHeader className={getOemStyles()}>
                    新しいパスワード
                    <Image src={requiredMarkSrc} alt="必須" width={41} height={16} style={{ marginLeft: "5px", float: "right", verticalAlign: "middle" }} />
                  </s.InfoTableHeader>
                  <s.InfoTableCell>
                    {formProps.input_password({
                      name: "newPassword",
                      label: "",
                    })}
                    {/* <s.InputField type="password" value={form.newPassword || ""} onChange={(e) => formProps.setForm({ ...form, newPassword: e.target.value })} style={{ imeMode: "inactive" }} /> */}
                    <s.PasswordNote>※半英数字8文字</s.PasswordNote>
                  </s.InfoTableCell>
                </tr>
                <tr>
                  <s.InfoTableHeader className={getOemStyles()}>
                    新しいパスワード2
                    <Image src={requiredMarkSrc} alt="必須" width={41} height={16} style={{ marginLeft: "5px", float: "right", verticalAlign: "middle" }} />
                  </s.InfoTableHeader>
                  <s.InfoTableCell>
                    {formProps.input_password({
                      name: "newPassword2",
                      label: "",
                    })}
                    {/* <s.InputField type="password" value={form.newPassword2 || ""} onChange={(e) => formProps.setForm({ ...form, newPassword2: e.target.value })} style={{ imeMode: "inactive" }} /> */}
                    <s.PasswordNote>※半英数字8文字</s.PasswordNote>
                  </s.InfoTableCell>
                </tr>
              </tbody>
            </s.InfoTable>

            <s.ButtonContainer>
              <s.ChangeButton className={getOemStyles()} onClick={handleChangePassword} disabled={form.isSubmitting}>
                パスワードを変更
              </s.ChangeButton>
            </s.ButtonContainer>
          </s.ResponsiveContainer>

          {/* ローディングオーバーレイ */}
          {form.isLoading && (
            <s.LoadingOverlay>
              <s.LoadingSpinner />
            </s.LoadingOverlay>
          )}
        </DialogContent>
      </s.AccountInfoContainer>
    </Dialog>
  );
};

export default AccountInfoDialog;
